import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import UiKit from '../index';
import Slider from './Slider';
import styled from 'styled-components';
import { datesUtil } from '../../../infrastructure/utils/dates-util';

interface AudioPlayerProps {
  src?: Url;
  className?: string;
  duration?: NumberOfSeconds;
  showDuration?: boolean;
  onPlay?: () => void;
  loading?: boolean;
  preload?: 'metadata' | 'none';
  disabled?: boolean;
  withProgress?: boolean;
}

// TODO remove calculated duration?
function AudioPlayer({
  src,
  className,
  duration,
  showDuration,
  onPlay,
  loading,
  preload = 'metadata',
  disabled,
  withProgress,
}: AudioPlayerProps): ReactElement {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState<NumberOfSeconds>(0 as NumberOfSeconds);
  const [audioDuration, setAudioDuration] = useState<NumberOfSeconds>(0 as NumberOfSeconds);
  const playerRef = useRef<HTMLAudioElement>(null);
  const effectiveDuration = (duration ?? audioDuration) as NumberOfSeconds;
  const formattedDuration = useMemo(
    () =>
      datesUtil.secondsToFormattedTime(
        Math.floor(effectiveDuration as number) as NumberOfSeconds,
        'mm:ss',
      ),
    [effectiveDuration],
  );
  const formattedCurrentTime = useMemo(
    () => datesUtil.secondsToFormattedTime(Math.floor(currentTime) as NumberOfSeconds, 'mm:ss'),
    [currentTime],
  );
  const timeLabel = withProgress
    ? `${formattedCurrentTime} / ${formattedDuration}`
    : duration
    ? formattedDuration
    : showDuration
    ? formattedCurrentTime
    : '';

  useEffect(() => {
    if (playerRef.current) {
      if (isPlaying && src && !loading) {
        playerRef.current && playerRef.current.play();
      } else if (src && !loading) {
        playerRef.current && playerRef.current.pause();
      }
    }
  }, [src, playerRef, isPlaying, loading]);

  if (loading && isPlaying) {
    return (
      <Wrapper className={className}>
        <UiKit.IconButton icon={<UiKit.icons.Loading />}></UiKit.IconButton>
      </Wrapper>
    );
  }

  const handleSeek = (next: number) => {
    if (playerRef.current) {
      playerRef.current.currentTime = next;
    }
    setCurrentTime(next as NumberOfSeconds);
  };

  return (
    <Wrapper className={className}>
      {!withProgress && timeLabel}{' '}
      {isPlaying ? (
        <UiKit.IconButton
          icon={<UiKit.icons.PauseCircle />}
          onClick={() => {
            setIsPlaying(false);
          }}
        />
      ) : (
        <UiKit.IconButton
          disabled={disabled}
          icon={<UiKit.icons.PlayCircle />}
          onClick={() => {
            setIsPlaying(true);
            onPlay && onPlay();
          }}
        />
      )}
      {withProgress && (
        <ProgressSlider
          value={Math.floor(currentTime)}
          max={Math.max(1, Math.floor(effectiveDuration as number))}
          step={1}
          disabled={disabled || !src || !effectiveDuration}
          showTooltip={false}
          onChange={handleSeek}
        />
      )}
      {withProgress && <TimeLabel>{timeLabel}</TimeLabel>}
      {!!src && (
        <audio
          preload={preload}
          src={src}
          ref={playerRef}
          onLoadedMetadata={event =>
            setAudioDuration(event.currentTarget.duration as NumberOfSeconds)
          }
          onEnded={() => {
            setIsPlaying(false);
            setCurrentTime(0 as NumberOfSeconds);
          }}
          onTimeUpdate={event => setCurrentTime(event.currentTarget.currentTime as NumberOfSeconds)}
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressSlider = styled(Slider)`
  flex: 1;
  min-width: 120px;
  margin: 0;
`;

const TimeLabel = styled.span`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export default AudioPlayer;
